import { lazy, Suspense } from 'react';
import HomeForSignedIn from './components/HomeForSignedIn';
import StudioShell from './components/AIAgent/StudioShell';
import { GUIDE_LOADERS } from './growth/content/guideLoaders';
import { IDEA_LOADERS } from './growth/content/ideas/loaders';
import { FEATURE_PAGE_IDS, FEATURE_PAGE_LOADERS } from './growth/content/featurePages';
import { COMPARE_LOADERS } from './growth/content/compare/index.js';
import { loadLocaleContent } from './growth/content/localeContent';
import { loadGlossary } from './growth/content/glossaryContent';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Navigation from './components/Navigation';
import { useLoadNewBuildOnNavigation, useNewBuildAvailable } from './utils/buildFreshness';
import LanguageSuggestion from './components/LanguageSuggestion';
import SeasonalBanner from './components/SeasonalBanner';
import ReferralClaim from './growth/ReferralClaim';
import SignInSteps from './components/auth/SignInSteps';
import SignupIntentRecovery from './growth/SignupIntentRecovery';
import CheckoutComeback from './growth/CheckoutComeback';
import PendingRemix from './components/Remix/PendingRemix';
import ProWall from './components/upgrade/ProWall';
import SignupSource from './growth/SignupSource';
import AffiliateClaim from './growth/AffiliateClaim';
import ConnectionBanner from './components/ConnectionBanner';
import AppSidebar from './components/AppShell/AppSidebar';
import { SidebarProvider, useSidebar } from './components/AppShell/SidebarContext';
import { AuthProvider, SubscriptionProvider, PollingProvider, ThemeProvider as CustomThemeProvider, ConsentProvider } from './context';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { Toaster as ShadcnToaster } from "@/components/ui/toaster";
import { ConsentManager } from './components/Consent/ConsentManager';
import { useLocale } from './i18n/LocaleContext';
import { dirFor } from './i18n/locales';
import { DirectionProvider } from '@radix-ui/react-direction';
import { PAGES, langTagFor } from './i18n/routes';
import { COMMUNITY_PATH } from './growth/communityPaths';
import { ROUTE_ROOT_ATTR, prerenderedHtml } from './utils/prerenderedPage';

// Loading fallback component. It holds a whole screen's height so the footer
// never paints under the nav and then drops when the page's chunk mounts (one
// layout shift of 0.33 at 1440 on a route with no prerendered page, measured
// 2026-09-24)
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// While the page's chunk loads on the first visit, the page as it was prerendered
// (utils/prerenderedPage.js), so nothing blanks or moves; a spinner otherwise
const RouteFallback = ({ pathname }: { pathname: string }) => {
  const html = prerenderedHtml(pathname);
  return html
    ? <div style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: html }} />
    : <PageLoader />;
};

// The footer is below the fold on every page, and carries the free tools' and the
// checker's copy in four languages for their titles: it loads after the page does
const SiteFooter = lazy(() => import('./components/SiteFooter'));
// On these pages the footer is one line (SiteFooter.AUTH_PAGE), so it reserves one line
const AUTH_PAGE = /^\/(login|signup)(\/|$)/;

// Lazy load all page components
const LandingV3 = lazy(() => import('./pages/LandingV3'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailsPage = lazy(() => import('./pages/ProjectDetailsPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/SubscriptionSuccessPage'));
const SubscriptionCancelPage = lazy(() => import('./pages/SubscriptionCancelPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const EditorPage = lazy(() => import('./pages/EditorPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AIAgentPage = lazy(() => import('./pages/AIAgentPage'));
const AIAgentProjectDetailsPage = lazy(() => import('./pages/AIAgentProjectDetailsPage'));
const SeriesPage = lazy(() => import('./pages/SeriesPage'));
const CharactersPage = lazy(() => import('./pages/CharactersPage'));
const SeriesCreatePage = lazy(() => import('./pages/SeriesCreatePage'));
const SeriesDetailPage = lazy(() => import('./pages/SeriesDetailPage'));
const SceneStudioPage = lazy(() => import('./pages/SceneStudioPage'));

// Lazy load product pages
const ProductAIClippingPage = lazy(() => import('./pages/ProductsV3').then((m) => ({ default: m.ClippingV3 })));
const ProductAutoCaptionPage = lazy(() => import('./pages/ProductsV3').then((m) => ({ default: m.CaptionV3 })));
const ProductVideoReframePage = lazy(() => import('./pages/ProductsV3').then((m) => ({ default: m.ReframeV3 })));
const ProductAIAgentPage = lazy(() => import('./pages/ProductsV3').then((m) => ({ default: m.AgentV3 })));
const ProductEditorPage = lazy(() => import('./pages/ProductsV3').then((m) => ({ default: m.EditorV3 })));

// Lazy load FAQ and Compare pages
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ComparePage = lazy(() => import('./pages/compare/ComparePage'));
// /compare/<tool> became /alternatives/<tool>; a link still pointing at one lands on the new page
function CompareRedirect() {
  const { id } = useParams();
  return <Navigate to={`/alternatives/${id}`} replace />;
}

// Lazy load "For" pages (use cases)
const ForPage = lazy(() => import('./pages/for/ForPage'));
const ForTikTokPage = lazy(() => import('./pages/for/TikTokPage'));
const ForYouTubeShortsPage = lazy(() => import('./pages/for/YouTubeShortsPage'));
const ForInstagramReelsPage = lazy(() => import('./pages/for/InstagramReelsPage'));
const ForPodcastersPage = lazy(() => import('./pages/for/PodcastersPage'));
const ForStreamersPage = lazy(() => import('./pages/for/StreamersPage'));

// The Spanish and Portuguese pages, by their key in i18n/routes.js: each is routed
// at every translated path the map lists for it
const LocalizedHomePage = lazy(() => import('./pages/localized/HomePage'));
const LocalizedAIAgentPage = lazy(() => import('./pages/localized/AIAgentLandingPage'));
const LocalizedPricingPage = lazy(() => import('./pages/localized/PricingPage'));
const LocalizedFAQPage = lazy(() => import('./pages/localized/FAQPage'));
const LocalizedFeaturePage = lazy(() => import('./pages/localized/FeatureLandingPage'));
// Topic, format, country and legend pages load their own language's copy with the page, so the
// prerendered page stays up until both are there (growth/content/localeContent.js)
function withLocaleContent<P extends object>(load: () => Promise<{ default: React.ComponentType<P> }>, loadLocale: (locale: string) => Promise<unknown> = loadLocaleContent) {
  const byLocale: Record<string, React.LazyExoticComponent<React.ComponentType<P>>> = {};
  return (locale: string) => (byLocale[locale] ||= lazy(() => Promise.all([load(), loadLocale(locale)]).then(([page]) => page)));
}
const nichePage = withLocaleContent(() => import('./pages/localized/NichePage'));
// The season's own page (the October banner and the Halloween ad land there): its own chunk, so no
// other topic page carries it
const halloweenPage = withLocaleContent(() => import('./pages/localized/HalloweenPage'));
const legendPage = withLocaleContent(() => import('./pages/localized/LegendPage'));
const TurkishPage = lazy(() => import('./pages/localized/TurkishPage'));
const glossaryPage = withLocaleContent(() => import('./pages/localized/GlossaryPage'), loadGlossary);
const ToolPage = lazy(() => import('./pages/localized/ToolPage'));
// A guide's page loads its own guide only, with the page, so the prerendered page stays up
// until both are there (growth/content/guideLoaders.js)
const guidePages: Record<string, React.LazyExoticComponent<() => React.JSX.Element>> = {};
function guidePage(id: string) {
  const load = GUIDE_LOADERS[id as keyof typeof GUIDE_LOADERS];
  if (!load) return null;
  guidePages[id] ||= lazy(() => Promise.all([import('./pages/localized/GuidePage'), load()])
    .then(([page, guide]) => ({ default: () => <page.default id={id} guide={guide.default} /> })));
  const Page = guidePages[id];
  return <Page />;
}
// An idea library page loads its own language's page only, with the page (growth/content/ideas/loaders.js)
const ideaPages: Record<string, React.LazyExoticComponent<() => React.JSX.Element>> = {};
function ideaPage(locale: string, id: string) {
  const load = (IDEA_LOADERS as Record<string, Record<string, () => Promise<{ default: unknown }>>>)[locale]?.[id];
  if (!load) return null;
  ideaPages[`${locale}.${id}`] ||= lazy(() => Promise.all([import('./pages/localized/IdeaLibraryPage'), load()])
    .then(([page, content]) => ({ default: () => <page.default id={id} page={content.default} /> })));
  const Page = ideaPages[`${locale}.${id}`];
  return <Page />;
}
// A "Clip-Short vs <tool>" page loads its own tool's words only, with the page (growth/content/compare/)
const comparePages: Record<string, React.LazyExoticComponent<() => React.JSX.Element>> = {};
function comparePage(id: string) {
  const load = COMPARE_LOADERS[id as keyof typeof COMPARE_LOADERS];
  if (!load) return null;
  comparePages[id] ||= lazy(() => Promise.all([import('./pages/localized/ComparePage'), load()])
    .then(([page, content]) => ({ default: () => <page.default id={id} page={content.default} /> })));
  const Page = comparePages[id];
  return <Page />;
}
const CheckerPage = lazy(() => import('./pages/localized/CheckerPage'));
const VoicePage = lazy(() => import('./pages/localized/VoicePage'));
// Step-by-step tutorials with the app's own screens (growth/content/tutorials.js)
const TutorialPage = lazy(() => import('./pages/localized/TutorialPage'));
const CalculatorPage = lazy(() => import('./pages/localized/CalculatorPage'));
// The free tools that read the visitor's file in their browser (growth/content/fileTools.js)
const FileToolPage = lazy(() => import('./pages/localized/FileToolPage'));
const AlternativePage = lazy(() => import('./pages/localized/AlternativePage'));
const CaptionLanguagePage = lazy(() => import('./pages/localized/CaptionLanguagePage'));
const ToolsHubPage = lazy(() => import('./pages/localized/ToolsHubPage'));
const SeriesLandingPage = lazy(() => import('./pages/localized/SeriesLandingPage'));
// A feature page loads its own words only, with the page (growth/content/featurePages/)
const featurePages: Record<string, React.LazyExoticComponent<() => React.JSX.Element>> = {};
function featurePage(key: string) {
  const id = FEATURE_PAGE_IDS[key as keyof typeof FEATURE_PAGE_IDS];
  const load = FEATURE_PAGE_LOADERS[id as keyof typeof FEATURE_PAGE_LOADERS];
  if (!load) return null;
  featurePages[id] ||= lazy(() => Promise.all([import('./pages/localized/FeaturePromoPage'), load()])
    .then(([page, content]) => ({ default: () => <page.default page={content.default} /> })));
  const Page = featurePages[id];
  return <Page />;
}
const AffiliatesPage = lazy(() => import('./pages/localized/AffiliatesPage'));
const AboutPage = lazy(() => import('./pages/localized/AboutPage'));
// Every published example video with its idea (growth/content/gallery.js)
const ExamplesGalleryPage = lazy(() => import('./pages/localized/ExamplesGalleryPage'));
const SharedVideoPage = lazy(() => import('./pages/SharedVideoPage'));
const CommunityPage = lazy(() => import('./pages/localized/CommunityPage'));
const CommunityVideoPage = lazy(() => import('./pages/localized/CommunityPage').then((m) => ({ default: m.CommunityVideoPage })));
const LOCALIZED_ROUTES = {
  home: LocalizedHomePage,
  aiAgent: LocalizedAIAgentPage,
  pricing: LocalizedPricingPage,
  faq: LocalizedFAQPage,
  compare: ComparePage,
  aiClipping: () => <LocalizedFeaturePage feature="aiClipping" />,
  videoReframe: () => <LocalizedFeaturePage feature="videoReframe" />,
  affiliates: AffiliatesPage,
  about: AboutPage,
};
// A page key's element: the page above, or a niche/legend page for "niche.<id>"/"legend.<id>"
function localizedElement(key: string, locale: string) {
  const [kind, id] = key.split('.');
  const NichePage = nichePage(locale);
  const LegendPage = legendPage(locale);
  if (key === 'niche.halloween') {
    const HalloweenPage = halloweenPage(locale);
    return <HalloweenPage />;
  }
  if (kind === 'niche') return <NichePage niche={id} />;
  if (kind === 'legend') return <LegendPage id={id} />;
  if (kind === 'tr') return <TurkishPage id={id} />;
  if (kind === 'tools') return <ToolPage tool={id} />;
  if (kind === 'guide') return guidePage(id);
  if (kind === 'ideas') return ideaPage(locale, id);
  if (kind === 'checker') return <CheckerPage />;
  if (kind === 'voices') return <VoicePage id={id} />;
  if (kind === 'tutorial') return <TutorialPage id={id} />;
  if (kind === 'calc') return <CalculatorPage id={id} />;
  if (kind === 'file') return <FileToolPage id={id} />;
  if (kind === 'alt') return <AlternativePage id={id} />;
  if (kind === 'vs') return comparePage(id);
  if (kind === 'glossary') {
    const GlossaryPage = glossaryPage(locale);
    return <GlossaryPage id={id} />;
  }
  if (kind === 'captions') return <CaptionLanguagePage id={id} />;
  if (kind === 'hub') return <ToolsHubPage />;
  if (kind === 'gallery') return <ExamplesGalleryPage />;
  if (key === 'feature.series') return <SeriesLandingPage />;
  if (kind === 'feature') return featurePage(key);
  if (kind === 'country') return <NichePage niche={id} pageKey={key} />;
  if (kind === 'format') return <NichePage niche={id} pageKey={key} />;
  const Page = LOCALIZED_ROUTES[key as keyof typeof LOCALIZED_ROUTES];
  return Page ? <Page /> : null;
}
// English versions of the four pages above are written out below; the rest
// (niche, legend, tools, guide, country) exist only here, English included
const localizedRoutes = Object.entries(PAGES).flatMap(([key, paths]) =>
  Object.entries(paths as Record<string, string>)
    .filter(([locale]) => locale !== 'en' || key.includes('.'))
    .map(([locale, path]) => <Route key={`${key}-${locale}`} path={path} element={localizedElement(key, locale)} />)
);

// ThemedApp component that consumes the theme context
function ThemedApp() {
  const location = useLocation();
  const sidebar = useSidebar();
  const { locale } = useLocale();
  const isEditorPage = location.pathname.startsWith('/editor');
  // A tab that was open across a deploy loads the new build on its next page
  // change, rather than asking for chunks that deploy took away
  useLoadNewBuildOnNavigation(location.pathname, { available: useNewBuildAvailable() });
  // Work surfaces keep the window for the work: no marketing footer
  // (every page of the AI Agent's studio, its first one included: the footer showed under
  // the video page alone, because only `/ai-agent/…` was matched)
  const isAIAgentPage =
    /^\/ai-agent(\/|$)/.test(location.pathname) ||
    /^\/(ai-clipping|auto-caption|video-reframe)\/[^/]+/.test(location.pathname);
  // No marketing footer under a page that sits beside the app's sidebar, and no room kept for
  // one either: the empty placeholder still stood 400 px tall under the tool pages, so every
  // one scrolled into a blank black band (the user, 2026-10-11: 'fix scrolling')
  const noFooter = isEditorPage || isAIAgentPage || sidebar.shown;
  
  return (
    // Radix reads its direction from this, not from <html dir>: menus, selects and
    // sliders place and key themselves right to left on an Arabic page
    <DirectionProvider dir={dirFor(locale)}>
    {/* The theme's class is on <html> (index.html before the first paint, ThemeContext after),
        where Radix portals see it too */}
    <div>
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        {/* <html lang> and dir follow the page: /es pages say es, /ar pages are ar-EG (a page
            written for one country says its own, ar-SA, pt-PT: i18n/routes.js PAGE_TAGS) and
            read right to left, and the prerender keeps both. dir sits on <html> so the
            dialogs and menus Radix mounts on <body> flip with the page */}
        <Helmet htmlAttributes={{ lang: langTagFor(location.pathname, locale), dir: dirFor(locale) }} />
        <Navigation />
        <AppSidebar />
        <ConsentManager />
        <ReferralClaim />
        <SignInSteps />
        <SignupIntentRecovery />
        <CheckoutComeback />
        <PendingRemix />
        <ProWall />
        <SignupSource />
        <AffiliateClaim />
        <ConnectionBanner />
        <main className="flex-1 w-full bg-background mt-16 lg:ps-[var(--app-rail,0px)]">
          <LanguageSuggestion />
          <SeasonalBanner />
          <div {...{ [ROUTE_ROOT_ATTR]: '' }} style={{ display: 'contents' }}>
          <Suspense fallback={<RouteFallback pathname={location.pathname} />}>
            <Routes>
            <Route path="/" element={<HomeForSignedIn><LandingV3 /></HomeForSignedIn>} />
            {localizedRoutes}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/*" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/*" element={<SignupPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/affiliates" element={<AffiliatesPage />} />
            <Route path="/about" element={<AboutPage />} />
            {/* A video its owner shared: public, never indexed */}
            <Route path="/v/:id" element={<SharedVideoPage />} />
            {/* The community gallery: videos their owners listed and an admin approved, indexed (growth/communityHtml.js) */}
            {Object.entries(COMMUNITY_PATH).flatMap(([locale, path]) => [
              <Route key={`community-${locale}`} path={path} element={<CommunityPage locale={locale} />} />,
              <Route key={`community-video-${locale}`} path={`${path}/:id`} element={<CommunityVideoPage locale={locale} />} />,
            ])}

            {/* Product Pages */}
            <Route path="/features/ai-clipping" element={<ProductAIClippingPage />} />
            <Route path="/features/auto-caption" element={<ProductAutoCaptionPage />} />
            <Route path="/features/video-reframe" element={<ProductVideoReframePage />} />
            <Route path="/features/ai-agent" element={<ProductAIAgentPage />} />
            <Route path="/features/editor" element={<ProductEditorPage />} />

            {/* FAQ and Compare Pages */}
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/compare" element={<ComparePage />} />
            {/* The old comparison pages are each tool's alternative page now (vercel.json redirects them too) */}
            <Route path="/compare/:id" element={<CompareRedirect />} />

            {/* Use Case Pages */}
            <Route path="/for" element={<ForPage />} />
            <Route path="/for/tiktok" element={<ForTikTokPage />} />
            <Route path="/for/youtube-shorts" element={<ForYouTubeShortsPage />} />
            <Route path="/for/instagram-reels" element={<ForInstagramReelsPage />} />
            <Route path="/for/podcasters" element={<ForPodcastersPage />} />
            <Route path="/for/streamers" element={<ForStreamersPage />} />

            <Route path="/account" element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            } />
            <Route path="/ai-clipping" element={
              <ProtectedRoute>
                <ProjectsPage feature="ai-clipping" />
              </ProtectedRoute>
            } />
            <Route path="/ai-clipping/new" element={<Navigate to="/ai-clipping" replace />} />
            <Route path="/ai-clipping/:id" element={
              <ProtectedRoute>
                <ProjectDetailsPage feature="ai-clipping" />
              </ProtectedRoute>
            } />
            <Route path="/auto-caption" element={
              <ProtectedRoute>
                <ProjectsPage feature="auto-caption" />
              </ProtectedRoute>
            } />
            <Route path="/auto-caption/new" element={<Navigate to="/auto-caption" replace />} />
            <Route path="/auto-caption/:id" element={
              <ProtectedRoute>
                <ProjectDetailsPage feature="auto-caption" />
              </ProtectedRoute>
            } />
            <Route path="/video-reframe" element={
              <ProtectedRoute>
                <ProjectsPage feature="reframe" />
              </ProtectedRoute>
            } />
            <Route path="/video-reframe/new" element={<Navigate to="/video-reframe" replace />} />
            <Route path="/video-reframe/:id" element={
              <ProtectedRoute>
                <ProjectDetailsPage feature="reframe" />
              </ProtectedRoute>
            } />
            {/* The AI Agent as a studio: its modes in a rail on the left, the pages beside it (components/AIAgent/StudioShell.jsx) */}
            <Route element={<StudioShell />}>
              <Route path="/ai-agent" element={
                <ProtectedRoute>
                  <AIAgentPage />
                </ProtectedRoute>
              } />
              {/* Scenes as a place of their own: one animated shot with voices (pages/SceneStudioPage.jsx) */}
              <Route path="/ai-agent/scenes" element={
                <ProtectedRoute>
                  <SceneStudioPage />
                </ProtectedRoute>
              } />
              {/* AI Agent videos drafted on a schedule, each waiting for review (pages/SeriesPage.jsx) */}
              <Route path="/ai-agent/series" element={
                <ProtectedRoute>
                  <SeriesPage />
                </ProtectedRoute>
              } />
              {/* Starting or changing a series, with its first episodes shown (pages/SeriesCreatePage.jsx) */}
              <Route path="/ai-agent/series/new" element={
                <ProtectedRoute>
                  <SeriesCreatePage />
                </ProtectedRoute>
              } />
              {/* One series: its numbers, every episode, its Up next (pages/SeriesDetailPage.jsx) */}
              <Route path="/ai-agent/series/:id" element={
                <ProtectedRoute>
                  <SeriesDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/ai-agent/series/:id/edit" element={
                <ProtectedRoute>
                  <SeriesCreatePage />
                </ProtectedRoute>
              } />
              {/* The recurring cast every video and series can use (pages/CharactersPage.jsx) */}
              <Route path="/ai-agent/characters" element={
                <ProtectedRoute>
                  <CharactersPage />
                </ProtectedRoute>
              } />
            </Route>
            <Route path="/ai-agent/:id" element={
              <ProtectedRoute>
                <AIAgentProjectDetailsPage />
              </ProtectedRoute>
            } />
            <Route path="/projects" element={<Navigate to="/ai-clipping" replace />} />
            <Route path="/projects/new" element={<Navigate to="/ai-clipping" replace />} />
            <Route path="/projects/:id" element={<Navigate to="/ai-clipping/:id" replace />} />
            <Route path="/subscription/success" element={
              <ProtectedRoute>
                <SubscriptionSuccessPage />
              </ProtectedRoute>
            } />
            <Route path="/subscription/cancel" element={
              <ProtectedRoute>
                <SubscriptionCancelPage />
              </ProtectedRoute>
            } />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/editor/:projectId?/:shortId?" element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            } />
            {/* Last: a path with no page gets one saying so (noindex), not an empty page */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          </div>
        </main>
        <footer
          className="border-t bg-background"
          style={{
            minHeight: noFooter ? '0px' : AUTH_PAGE.test(location.pathname) ? '96px' : '400px',
            visibility: noFooter ? 'hidden' : 'visible',
            position: noFooter ? 'absolute' : 'relative',
            pointerEvents: noFooter ? 'none' : 'auto'
          }}
        >
          {!noFooter && <Suspense fallback={null}><SiteFooter /></Suspense>}
        </footer>
      </div>
      <ShadcnToaster />
    </div>
    </DirectionProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ConsentProvider>
        <CustomThemeProvider>
          <AuthProvider>
            <SubscriptionProvider>
              <PollingProvider>
                <SidebarProvider>
                  <ThemedApp />
                </SidebarProvider>
              </PollingProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </CustomThemeProvider>
      </ConsentProvider>
    </HelmetProvider>
  );
}

export default App; 